import { httpClient } from "../funcs"
import { ensureDemoServicesTemplate } from "./demoServicesTemplate"
import { POSUTKA } from "./workWithObjectEntity"

export { POSUTKA }

const SESSION_KEY = "okidoki_demo_send_to_self_v1"
const SIGNED_RETURN_KEY = "okidoki_demo_signed_return_v1"

export const DEMO_SIGNED_RETURN_TITLE = "Демо подписано"
export const DEMO_SIGNED_RETURN_TEXT =
    "Вы только что прошли путь клиента. Вот ваш договор."

export function demoSignedHandoff(business?: string | null): { intro: string; steps: string[] } {
    if (business === POSUTKA) {
        return {
            intro: "Теперь ваш первый настоящий договор:",
            steps: ["заведите объект", "выберите его в договоре", "проверьте цену и даты"],
        }
    }
    return {
        intro: "Дальше загрузите свой Word или сгенерируйте договор. Этот пример можно не использовать.",
        steps: [],
    }
}

export const DEMO_OWN_UPLOAD_CTA = "Загрузить свой шаблон"
export const DEMO_OWN_GENERATE_CTA = "Сгенерировать"
export const DEMO_OWN_LATER_CTA = "Позже"

export function demoSignedHandoffLines(business?: string | null): string[] {
    const { intro, steps } = demoSignedHandoff(business)
    return [intro, ...steps.map((step, i) => `${i + 1}) ${step}`)]
}

export function demoSignedNextCta(business?: string | null): string {
    return business === POSUTKA ? "Добавьте свой объект" : DEMO_OWN_UPLOAD_CTA
}

export function markDemoSendToSelf() {
    try {
        sessionStorage.setItem(SESSION_KEY, "1")
    } catch {
        // ignore
    }
}

export function isDemoSendToSelf(): boolean {
    try {
        return sessionStorage.getItem(SESSION_KEY) === "1"
    } catch {
        return false
    }
}

export function isDemoQuery(params?: Record<string, unknown> | null): boolean {
    const demo = params?.demo
    return demo === "1" || demo === true || demo === 1
}

/** Демо на подписании только по ?demo=1. Session-флаг не должен включать демо на чужом /contract/*. */
export function isDemoFlow(params?: Record<string, unknown> | null): boolean {
    return isDemoQuery(params)
}

export function clearDemoSendToSelf() {
    try {
        sessionStorage.removeItem(SESSION_KEY)
    } catch {
        // ignore
    }
}

export function markDemoSignedReturn() {
    try {
        sessionStorage.setItem(SIGNED_RETURN_KEY, "1")
    } catch {
        // ignore
    }
}

export function peekDemoSignedReturn(): boolean {
    try {
        return sessionStorage.getItem(SIGNED_RETURN_KEY) === "1"
    } catch {
        return false
    }
}

export function consumeDemoSignedReturn(): boolean {
    try {
        if (sessionStorage.getItem(SIGNED_RETURN_KEY) !== "1") {
            return false
        }
        sessionStorage.removeItem(SIGNED_RETURN_KEY)
        return true
    } catch {
        return false
    }
}

const SERVICES_TEMPLATE_RE = /услуг|оказан|работ/i
const RENTAL_TEMPLATE_RE = /квартир|найм|аренд|посуточ|заезд|выезд|ноч/i

function templateCatalogText(sectionName: unknown, template: any): string {
    return [sectionName, template?.name, template?.comment]
        .map((part) => String(part ?? ""))
        .join(" ")
}

function templateLooksLikeRental(sectionName: unknown, template: any): boolean {
    return RENTAL_TEMPLATE_RE.test(templateCatalogText(sectionName, template))
}

function templateLooksLikeServices(sectionName: unknown, template: any): boolean {
    if (templateLooksLikeRental(sectionName, template)) {
        return false
    }
    return SERVICES_TEMPLATE_RE.test(templateCatalogText(sectionName, template))
}

export function firstReadyTemplateShareLink(
    sections: any[],
    opts?: { preferServices?: boolean },
): string {
    const preferServices = Boolean(opts?.preferServices)
    for (const section of sections ?? []) {
        for (const template of section?.templates ?? []) {
            const id = template?.template_share_link
            if (!id) {
                continue
            }
            if (preferServices && !templateLooksLikeServices(section?.section_name, template)) {
                continue
            }
            return String(id)
        }
    }
    return ""
}

type OwnerNavigate = (name: string, params: Record<string, string>) => void

/** После демо-подписи открыть договор инициатором, без as_client — иначе GET гостем даст 403. */
export function navigateToDemoSignedContract(
    contractId: string | undefined | null,
    navigate?: OwnerNavigate,
) {
    markDemoSignedReturn()
    clearDemoSendToSelf()
    navigateToSignedContractAsOwner(contractId, navigate)
}

export function navigateToOwnSignedContract(
    contractId: string | undefined | null,
    navigate?: OwnerNavigate,
) {
    navigateToSignedContractAsOwner(contractId, navigate)
}

function navigateToSignedContractAsOwner(
    contractId: string | undefined | null,
    navigate?: OwnerNavigate,
) {
    const id = String(contractId ?? "").trim()
    if (navigate) {
        if (id) {
            navigate("contract", { contractId: id })
        } else {
            navigate("signed-contracts", {})
        }
        return
    }
    window.location.assign(id ? `/contract/${id}` : "/signed-contracts")
}

export async function startDemoFromReadyTemplate(
    navigate: (name: string, params: Record<string, string>) => void,
    opts?: { preferServices?: boolean },
): Promise<boolean> {
    try {
        const { data } = await httpClient.post("/ready-templates")
        const shareLinkId = firstReadyTemplateShareLink(Array.isArray(data) ? data : [], opts)
        let templateId = ""
        if (shareLinkId) {
            const { data: added } = await httpClient.post("/add-shared-template", {
                share_link_id: shareLinkId,
            })
            templateId = added?._id ? String(added._id) : ""
        } else if (opts?.preferServices) {
            templateId = await ensureDemoServicesTemplate()
        }
        if (!templateId) {
            clearDemoSendToSelf()
            return false
        }
        markDemoSendToSelf()
        navigate("new-contract", { act: "new", templateId, demo: "1" })
        return true
    } catch (error) {
        clearDemoSendToSelf()
        throw error
    }
}
